import { useState, useCallback } from 'react';
import api from '../api/axios';

export const useSync = () => {
  const [syncStatus, setSyncStatus] = useState({
    isInitialized: false,
    isConnected: false,
    isSyncing: false,
    lastSync: null,
    error: null
  });

  const initializeSync = useCallback(async (stagingUri, productionUri) => {
    try {
      setSyncStatus(prev => ({ ...prev, isSyncing: true, error: null }));
      
      const response = await api.post('/sync/initialize', {
        stagingUri,
        productionUri
      });
      
      setSyncStatus(prev => ({
        ...prev,
        isInitialized: true,
        isConnected: true,
        isSyncing: false,
        error: null
      }));
      
      return response.data;
    } catch (error) {
      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        error: error.response?.data?.message || 'Failed to initialize sync'
      }));
      throw error;
    }
  }, []);

  const compareData = useCallback(async () => {
    try {
      setSyncStatus(prev => ({ ...prev, isSyncing: true, error: null }));
      
      const response = await api.get('/sync/compare');
      
      setSyncStatus(prev => ({ ...prev, isSyncing: false }));
      
      return response.data;
    } catch (error) {
      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        error: error.response?.data?.message || 'Failed to compare data'
      }));
      throw error;
    }
  }, []);

  const syncCompany = useCallback(async (companyId, options = {}) => {
    try {
      setSyncStatus(prev => ({ ...prev, isSyncing: true, error: null }));
      
      const response = await api.post('/sync/companies', {
        companyIds: [companyId], // Send as array to match backend expectation
        ...options
      });
      
      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        lastSync: new Date().toISOString(),
        error: null
      }));
      
      return response.data;
    } catch (error) {
      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        error: error.response?.data?.message || 'Failed to sync company'
      }));
      throw error;
    }
  }, []);

  const syncProject = useCallback(async (projectId, options = {}) => {
    try {
      setSyncStatus(prev => ({ ...prev, isSyncing: true, error: null }));
      
      const response = await api.post('/sync/projects', {
        projectIds: [projectId], // Send as array to match backend expectation
        ...options
      });
      
      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        lastSync: new Date().toISOString(),
        error: null
      }));
      
      return response.data;
    } catch (error) {
      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        error: error.response?.data?.message || 'Failed to sync project'
      }));
      throw error;
    }
  }, []);

  const syncFlowSteps = useCallback(async (projectId, options = {}) => {
    try {
      setSyncStatus(prev => ({ ...prev, isSyncing: true, error: null }));
      
      const response = await api.post('/sync/flowsteps', {
        projectIds: [projectId], // Send as array to match backend expectation
        ...options
      });
      
      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        lastSync: new Date().toISOString(),
        error: null
      }));
      
      return response.data;
    } catch (error) {
      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        error: error.response?.data?.message || 'Failed to sync flow steps'
      }));
      throw error;
    }
  }, []);

  const closeSync = useCallback(async () => {
    try {
      await api.post('/sync/close');
      
      setSyncStatus({
        isInitialized: false,
        isConnected: false,
        isSyncing: false,
        lastSync: null,
        error: null
      });
    } catch (error) {
      console.error('Failed to close sync connections:', error);
    }
  }, []);

  const getSyncLogs = useCallback(async (filters = {}) => {
    try {
      const response = await api.get('/sync-logs', { params: filters });
      return response.data;
    } catch (error) {
      throw error;
    }
  }, []);

  return {
    syncStatus,
    initializeSync,
    compareData,
    syncCompany,
    syncProject,
    syncFlowSteps,
    closeSync,
    getSyncLogs
  };
};
